import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import "./SegmentedControl.css";

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  "aria-label"?: string;
}

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  className,
  "aria-label": ariaLabel = "View options",
}: SegmentedControlProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  );

  /*
   * The indicator is measured from the selected button rather than assuming
   * every option is the same width.
   *
   * A percentage split (100/n wide, translated by index × 100%) only lines up
   * when every label happens to be the same length. "List" and "Board" are
   * not, so the pill was drawn at the average width and spilled under the
   * neighbouring label.
   */
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  /*
   * Callers pass `options` as an inline array literal, so it is a new
   * reference on every render. Depending on the array itself would re-run the
   * effect forever — each run sets state, which renders, which makes another
   * new array. The labels are what actually affect layout, so key on those.
   */
  const optionsKey = options.map((o) => o.label).join(" ");

  const measure = useCallback(() => {
    const active = refs.current[activeIndex];
    if (!active) return;
    const next = { left: active.offsetLeft, width: active.offsetWidth };
    // Compared before setting: an unconditional set inside a ResizeObserver
    // is a loop waiting to happen.
    setIndicator((prev) => (prev && prev.left === next.left && prev.width === next.width ? prev : next));
  }, [activeIndex]);

  // Layout effect so the pill is correct on first paint rather than visibly
  // jumping into place after it.
  useLayoutEffect(() => {
    measure();
  }, [measure, optionsKey]);

  // Labels reflow when the container resizes or a webfont finishes loading,
  // and a measurement taken before either is stale.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(track);
    for (const node of refs.current) if (node) observer.observe(node);
    return () => observer.disconnect();
  }, [measure, optionsKey]);

  // Tab-style keyboard semantics: arrow keys move selection and focus
  // together, Home/End jump to the ends. Matches native OS segmented
  // controls and the ARIA "tablist" pattern.
  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % options.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + options.length) % options.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = options.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      const option = options[nextIndex];
      if (option) {
        onChange(option.value);
        refs.current[nextIndex]?.focus();
      }
    }
  }

  return (
    <div
      ref={trackRef}
      className={["ui-segmented", className ?? ""].filter(Boolean).join(" ")}
      role="tablist"
      aria-label={ariaLabel}
    >
      {/* Hidden until measured: a pill at the wrong width for one frame is
          more distracting than one that fades in correct. */}
      <span
        className="ui-segmented__indicator"
        style={{
          width: indicator ? `${indicator.width}px` : 0,
          transform: `translateX(${indicator ? indicator.left : 0}px)`,
          opacity: indicator ? 1 : 0,
        }}
        aria-hidden="true"
      />
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={["ui-segmented__option", selected ? "ui-segmented__option--on" : ""].filter(Boolean).join(" ")}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
